
import { api } from "./apiServices"; // Adjust path if apiServices.js is located elsewhere

/**
 * Fetches the detailed information for a specific category by its ID.
 * @param {string|number} categoryId - The ID of the category to fetch.
 * @returns {Promise<Object>} A promise that resolves to the category data.
 */
export const getCategoryDetailsById = async (categoryId,locale) => {
  try {
    const response = await api.get(`/admin/categories/by-id/${categoryId}`, {params: { locale: locale },});
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching category details for ID ${categoryId}:`,
      error
    );
    throw error; // Re-throw to allow caller to handle
  }
};

/**
 * Fetches a basic list of all categories.
 * Typically used for populating dropdowns or lists where full details are not required.
 * @returns {Promise<Array>} A promise that resolves to an array of basic category data.
 */
export const getBasicCategoryList = async () => {
  try {
    const response = await api.get("/admin/categories/basic-list");
    return response.data;
  } catch (error) {
    console.error("Error fetching basic category list:", error);
    throw error;
  }
};

/**
 * Updates an existing category with the provided payload.
 * @param {string|number} categoryId - The ID of the category to update.
 * @param {Object} payload - The data to update the category with.
 * @returns {Promise<Object>} A promise that resolves to the updated category data.
 */
export const updateCategory = async (categoryId, locale, payload) => {
  try {
    const response = await api.put(`/admin/categories/${categoryId}`, payload, {
      params: { locale: locale },
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating category with ID ${categoryId}:`, error);
    // You might want to parse error.response.data for specific backend messages
    throw error;
  }
};

// You can add other admin-related category API calls here in the future, e.g.:
// export const createCategory = async (payload) => { ... };
// export const deleteCategory = async (categoryId) => { ... };
/**
 * Fetches all invoices and credit notes for the admin dashboard.
 * Corresponds to the GET /admin/financials endpoint.
 */
export const getFinancials = () => {
  return api.get("/admin/financials/", {
    params: {
      // This prevents the browser from serving a stale, cached response
     // _cacheBust: new Date().getTime(),
    },
  });
};
/**
 * Initiates a refund for a specific invoice.
 * Corresponds to the POST /admin/financials/refunds/initiate endpoint.
 * @param {object} refundData - The data for the refund request.
 * @param {number} refundData.invoice_id - The ID of the invoice to refund.
 * @param {string} refundData.refund_type - 'full' or 'partial'.
 * @param {number|null} refundData.coins_to_refund - Number of coins for partial refund.
 * @param {string} refundData.reason - The reason for the refund.
 * @param {string|null} refundData.notes - Additional admin notes.
 */
export const initiateRefund = (refundData) => {
    return api.post('/admin/financials/refunds/initiate', refundData);
};


/**
 * Grants free lead responses to a seller.
 * @param {string|number} sellerProfileId - The ID of the seller profile.
 * @param {Object} payload - The request payload.
 * @param {number} payload.responses_to_grant - The number of free responses to grant.
 * @param {string} payload.reason - The reason for granting the responses.
 * @returns {Promise<Object>} A promise that resolves to the API response.
 */
export const grantFreeResponses = (sellerProfileId, payload) => {
    return api.post(`/admin/financials/${sellerProfileId}/grant-free-responses`,
      payload
    );
};

export const getFreeResponseHistory = (sellerProfileId) => {
    return api.get(`/admin/financials/${sellerProfileId}/free-response-history`);
};

/**
 * Fetches this seller's managed/unmanaged status transition history (who was
 * managing them and when), driven by the Zoho CRM ownership webhook.
 * @param {string|number} sellerProfileId - The ID of the seller.
 * @returns {Promise<Array>} A promise that resolves to the list of history entries.
 */
export const getSellerManagementStatusHistory = async (sellerProfileId) => {
    try {
        const response = await api.get(`/admin/sellers/${sellerProfileId}/management-status-history`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching management status history for seller ${sellerProfileId}:`, error);
        throw error;
    }
};

/**
 * Manually overrides this seller's managed/unmanaged status, independent of
 * the Zoho CRM ownership webhook. Requires the "sales:manages-seller-accounts"
 * permission. NOTE: the next Zoho ownership-change webhook call for this
 * seller can overwrite this override if it disagrees.
 * @param {string|number} sellerProfileId - The ID of the seller.
 * @param {boolean} isManaged - The managed status to set.
 * @param {string} reason - Mandatory justification for the override.
 * @returns {Promise<Object>} A promise that resolves to the updated seller info.
 */
export const setSellerManagedStatus = async (sellerProfileId, isManaged, reason) => {
    try {
        const response = await api.post(`/admin/sellers/${sellerProfileId}/set-managed-status`, {
            is_managed: isManaged,
            reason: reason,
        });
        return response.data;
    } catch (error) {
        console.error(`Error setting managed status for seller ${sellerProfileId}:`, error);
        throw error;
    }
};

/**
 * Reassigns this seller's Zoho CRM Account owner directly from the admin UI -
 * the mirror of the inbound Zoho ownership webhook. Pushes the new owner to
 * Zoho and recomputes is_managed_seller. Requires the
 * "sales:manages-seller-accounts" permission, and the target admin must have a
 * Zoho CRM user ID configured (the backend returns a 422 otherwise).
 * @param {string|number} sellerProfileId - The ID of the seller.
 * @param {number} newOwnerAdminId - The admin_users.id to assign as the new owner.
 * @returns {Promise<Object>} A promise that resolves to the updated seller info.
 */
export const assignSellerOwner = async (sellerProfileId, newOwnerAdminId) => {
    try {
        const response = await api.post(`/admin/sellers/${sellerProfileId}/assign-owner`, {
            new_owner_admin_id: newOwnerAdminId,
        });
        return response.data;
    } catch (error) {
        console.error(`Error assigning owner for seller ${sellerProfileId}:`, error);
        throw error;
    }
};



/**
 * Fetches the unified transaction ledger for a specific seller.
 * @param {string|number} sellerProfileId - The ID of the seller.
 * @param {number} page - The page number for pagination.
 * @param {number} pageSize - The number of items per page.
 * @returns {Promise<Object>} A promise that resolves to the paginated ledger data.
 */
export const getSellerLedger = async (sellerProfileId, page = 1, pageSize = 10) => {
  try {
    const response = await api.get(`/admin/sellers/${sellerProfileId}/ledger`, {
      params: {
        page: page,
        page_size: pageSize,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching ledger for seller ${sellerProfileId}:`, error);
    throw error;
  }
};

/**
 * Initiates a refund for a specific lead purchase.
 * @param {Object} payload - The data for the refund request.
 * @param {number} payload.lead_purchase_id - The ID of the lead purchase to refund.
 * @param {string} payload.reason - 'Lead Issue', 'Phone Number Issue', etc.
 * @param {string} payload.ticket_number - The associated support ticket number.
 * @param {string|null} payload.notes - Additional admin notes.
 * @param {string|null} payload.custom_reason - Required if reason is 'Other'.
 * @returns {Promise<Object>} A promise that resolves to the API response.
 */
export const initiateLeadRefund = async (payload) => {
  try {
    const response = await api.post('/admin/sellers/refund-lead', payload);
    return response.data;
  } catch (error) {
    console.error(`Error initiating lead refund:`, error);
    throw error;
  }
};



// --- NEW FUNCTIONS FOR REVIEW MODERATION ---

/**
 * Fetches the list of reviews awaiting moderation.
 * @param {string} status - Optional status to filter by ('PENDING' or 'UNDER_DISPUTE').
 * @returns {Promise<any>} The API response data.
 */
export const getModerationQueue = (status = '') => {
  const params = status ? { status } : {};
  return api.get('/admin/reviews/moderation-queue', { params });
};

/**
 * Approves or rejects a review.
 * @param {number} reviewId - The ID of the review to moderate.
 * @param {string} newStatus - The new status ('APPROVED' or 'REJECTED').
 * @param {string} moderationNotes - The admin's notes.
 * @returns {Promise<any>} The API response data.
 */
export const moderateReview = (reviewId, newStatus, moderationNotes) => {
  return api.post(`/admin/reviews/${reviewId}/moderate`, {
    new_status: newStatus,
    moderation_notes: moderationNotes,
  });
};

export const getSellerReviewsForAdmin = (sellerProfileId) => {
  // This endpoint needs to be created on the backend.
  // Assuming a path like '/admin/reviews/seller/{id}'
  return api.get(`/admin/reviews/seller/${sellerProfileId}`);
};
/**
 * Initiates the process to reissue a non-GST invoice as a GST invoice.
 * @param {string|number} invoiceId - The ID of the original invoice to reissue.
 * @returns {Promise<Object>} A promise that resolves to the API response.
 */
export const reissueInvoiceAsGst = (invoiceId) => {
  try {
    return api.post(`/admin/financials/invoices/${invoiceId}/reissue-as-gst`);
  } catch (error) {
    console.error(`Error reissuing invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};


// --- NEW FUNCTIONS FOR ADMIN USER MANAGEMENT ---

/**
 * Fetches the list of all admin users from the backend, including deactivated
 * ones, so the User Management page can find and reactivate them. Other
 * callers that want an active-only picker (e.g. coupon permissions) should
 * call GET /admin/users/all directly without this flag.
 * Corresponds to GET /admin/users/all?include_inactive=true
 * @returns {Promise<Array>} A promise that resolves to an array of admin user objects.
 */
export const getAllAdminUsers = async () => {
  try {
    const response = await api.get("/admin/users/all", { params: { include_inactive: true } });
    return response.data;
  } catch (error) {
    console.error("Error fetching admin users:", error);
    throw error;
  }
};

/**
 * Creates a new admin user by pre-provisioning them in the database.
 * The user will be fully activated on their first federated login.
 * Corresponds to POST /admin/users/create
 * @param {Object} userData - The data for the new user.
 * @param {string} userData.email - The email of the new user.
 * @param {string} userData.group - The admin group to assign (e.g., "SalesAgent").
 * @returns {Promise<Object>} A promise that resolves to the API response.
 */
export const createAdminUser = async (userData) => {
  try {
    const response = await api.post("/admin/users/create", userData);
    return response.data;
  } catch (error)
  {
    console.error("Error creating admin user:", error);
    // Pass the full error object so the UI can access response details
    throw error;
  }
};

/**
 * Fetches real marketplace leads for the sales team to cold-call, filtered by
 * category and location. Contact details always come back masked.
 * @param {Object} params
 * @param {number[]} params.categoryIds - localized category ids to match.
 * @param {Array<{scope_type: string, geo_point?: string, radius_km?: number, label?: string}>} params.locations - 1-5 location filters.
 * @param {string} [params.statusFilter] - "ELIGIBLE" (default) or "ALL".
 * @param {number} [params.page]
 * @param {number} [params.limit]
 */
export const getSalesCallLeads = async ({ categoryIds, locations, statusFilter = "ELIGIBLE", page = 1, limit = 25 }) => {
  try {
    const response = await api.get("/admin/leads/prospect-leads", {
      params: {
        category_ids: JSON.stringify(categoryIds),
        locations: JSON.stringify(locations),
        status_filter: statusFilter,
        page,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales call leads:", error);
    throw error;
  }
};

export const searchProspect = async (query, searchType) => {
  try {
    const response = await api.get("/admin/prospect/search", {
      params: { query, search_type: searchType },
    });
    return response.data;
  } catch (error) {
    console.error("Error searching prospect:", error);
    throw error;
  }
};

export const unsubscribeProspect = async (prospectId) => {
  try {
    const response = await api.post(`/admin/prospect/${prospectId}/unsubscribe`);
    return response.data;
  } catch (error) {
    console.error("Error unsubscribing prospect:", error);
    throw error;
  }
};

export const createProspectScrapeTask = async (payload) => {
  try {
    const response = await api.post("/admin/prospect/scrape-tasks", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating prospect scrape task:", error);
    throw error;
  }
};

export const listProspectScrapeTasks = async (params = {}) => {
  try {
    const response = await api.get("/admin/prospect/scrape-tasks", { params });
    return response.data;
  } catch (error) {
    console.error("Error listing prospect scrape tasks:", error);
    throw error;
  }
};

export const createProspectScrapeTasksBulk = async (payload) => {
  try {
    const response = await api.post("/admin/prospect/scrape-tasks/bulk", payload);
    return response.data;
  } catch (error) {
    console.error("Error bulk creating prospect scrape tasks:", error);
    throw error;
  }
};

export const listProspectDiscoveryQueue = async (params = {}) => {
  try {
    const response = await api.get("/admin/prospect/discovery-queue", { params });
    return response.data;
  } catch (error) {
    console.error("Error listing prospect discovery queue:", error);
    throw error;
  }
};

export const reviewProspectDiscoveryItem = async (queueId, payload) => {
  try {
    const response = await api.post(`/admin/prospect/discovery-queue/${queueId}/review`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error reviewing discovery row ${queueId}:`, error);
    throw error;
  }
};

export const listExternalCategoryMappings = async (params = {}) => {
  try {
    const response = await api.get("/admin/prospect/category-mappings", { params });
    return response.data;
  } catch (error) {
    console.error("Error listing external category mappings:", error);
    throw error;
  }
};

export const updateExternalCategoryMapping = async (mappingId, payload) => {
  try {
    const response = await api.put(`/admin/prospect/category-mappings/${mappingId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating category mapping ${mappingId}:`, error);
    throw error;
  }
};

export const deleteExternalCategoryMapping = async (mappingId) => {
  try {
    const response = await api.delete(`/admin/prospect/category-mappings/${mappingId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting category mapping ${mappingId}:`, error);
    throw error;
  }
};

export const listProspectLocalities = async (locationId) => {
  try {
    const response = await api.get(`/admin/prospect/locations/${locationId}/localities`);
    return response.data;
  } catch (error) {
    console.error(`Error listing localities for location ${locationId}:`, error);
    throw error;
  }
};

export const listBlacklistedDomains = async () => {
  try {
    const response = await api.get("/admin/prospect/blacklisted-domains");
    return response.data;
  } catch (error) {
    console.error("Error listing blacklisted domains:", error);
    throw error;
  }
};

export const createBlacklistedDomain = async (payload) => {
  try {
    const response = await api.post("/admin/prospect/blacklisted-domains", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating blacklisted domain:", error);
    throw error;
  }
};

export const deleteBlacklistedDomain = async (domainId) => {
  try {
    const response = await api.delete(`/admin/prospect/blacklisted-domains/${domainId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting blacklisted domain ${domainId}:`, error);
    throw error;
  }
};

// --- PROSPECT COMMS SETTINGS (includes signup free responses) ---
// These rows control both the prospect notification cadence and how many free responses
// a prospect receives on converting to a seller. Values resolve per field:
// category override -> parent category -> global -> backend default.

export const listProspectGlobalCommsSettings = async () => {
  try {
    const response = await api.get("/admin/prospect/comms-settings/global");
    return response.data;
  } catch (error) {
    console.error("Error listing global prospect comms settings:", error);
    throw error;
  }
};

export const updateProspectGlobalCommsSettings = async (channel, payload) => {
  try {
    const response = await api.put(`/admin/prospect/comms-settings/global/${channel}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating global prospect comms settings for ${channel}:`, error);
    throw error;
  }
};

export const listProspectCategoryCommsSettings = async (params) => {
  try {
    const response = await api.get("/admin/prospect/comms-settings/categories", { params });
    return response.data;
  } catch (error) {
    console.error("Error listing category prospect comms settings:", error);
    throw error;
  }
};

export const createProspectCategoryCommsSettings = async (payload) => {
  try {
    const response = await api.post("/admin/prospect/comms-settings/categories", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating category prospect comms settings:", error);
    throw error;
  }
};

export const updateProspectCategoryCommsSettings = async (settingId, payload) => {
  try {
    const response = await api.put(`/admin/prospect/comms-settings/categories/${settingId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating category prospect comms settings ${settingId}:`, error);
    throw error;
  }
};

export const deleteProspectCategoryCommsSettings = async (settingId) => {
  try {
    const response = await api.delete(`/admin/prospect/comms-settings/categories/${settingId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting category prospect comms settings ${settingId}:`, error);
    throw error;
  }
};

// Version history: every row ever written for a scope (active + deactivated), newest first.
export const getProspectGlobalCommsSettingsHistory = async (channel) => {
  try {
    const response = await api.get(`/admin/prospect/comms-settings/global/${channel}/history`);
    return response.data;
  } catch (error) {
    console.error(`Error loading global prospect comms settings history for ${channel}:`, error);
    throw error;
  }
};

export const getProspectCategoryCommsSettingsHistory = async (categoryId, channel) => {
  try {
    const response = await api.get(
      `/admin/prospect/comms-settings/categories/${categoryId}/history`,
      { params: { channel } }
    );
    return response.data;
  } catch (error) {
    console.error(`Error loading category prospect comms settings history for ${categoryId}:`, error);
    throw error;
  }
};

// --- NEW FUNCTION FOR AUTH STATUS CHECK ---

/**
 * Calls a protected endpoint to verify the user's auth status.
 * This triggers the JIT provisioning and group-syncing logic on the backend.
 * @returns {Promise<Object>} A promise that resolves to the user's status.
 */
export const checkAuthStatus = async () => {
  try {
    // The api service automatically includes the auth token in the headers.
    const response = await api.get("/auth/admin/check-status");
    return response.data;
  } catch (error) {
    console.error("Error checking auth status:", error);
    throw error;
  }
};

/**
 * Updates an existing admin user's editable fields (first/last name, group,
 * active status, Zoho CRM user ID, default-owner flag). Email is not editable.
 * Only include the fields you want to change - omitted fields are left as-is.
 * Corresponds to PUT /admin/users/{userId}
 * @param {number} userId - The database ID of the user to update.
 * @param {Object} payload
 * @param {string|null} [payload.first_name]
 * @param {string|null} [payload.last_name]
 * @param {string} [payload.group]
 * @param {boolean} [payload.is_active]
 * @param {string|null} [payload.zoho_user_id]
 * @param {boolean} [payload.is_default_owner]
 * @returns {Promise<Object>} A promise that resolves to the updated admin user.
 */
export const updateAdminUser = async (userId, payload) => {
  try {
    const response = await api.put(`/admin/users/${userId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating admin user ${userId}:`, error);
    throw error;
  }
};



/**
 * Fetches the comprehensive details for a specific lead for the admin panel.
 * @param {string|number} leadId - The ID of the lead to fetch.
 *- @returns {Promise<Object>} A promise that resolves to the lead detail data.
 */
export const getAdminLeadDetails = async (leadId) => {
  if (!leadId) {
    throw new Error("Lead ID is required.");
  }
  try {
    const response = await api.get(`/admin/lead-info/${leadId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching details for lead ${leadId}:`, error);
    throw error; // Re-throw to be handled by the calling component (e.g., React Query)
  }
};

/**
 * Updates the status of a specific lead.
 * @param {string|number} leadId - The ID of the lead to update.
 * @param {Object} payload - The update data.
 * @param {string} payload.new_status - The new status for the lead.
 * @param {string|null} payload.notes - The admin's notes for the change.
 * @returns {Promise<Object>} A promise that resolves to the updated lead data.
 */
export const updateLeadStatus = async (leadId, payload) => {
  try {
    const response = await api.put(`/admin/lead-info/${leadId}/status`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating status for lead ${leadId}:`, error);
    throw error;
  }
};

/**
 * Fetches the full communications history (WhatsApp + email) for a specific lead.
 * Includes delivery status, timestamps, and any inbound replies.
 * @param {string|number} leadId - The ID of the lead.
 * @returns {Promise<Object>} A promise that resolves to { lead_id, messages: [...] }.
 */
export const getLeadCommsDetails = async (leadId) => {
  if (!leadId) throw new Error("Lead ID is required.");
  try {
    const response = await api.get(`/admin/lead-info/${leadId}/comms`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching comms for lead ${leadId}:`, error);
    throw error;
  }
};

/**
 * Fetches the last 20 logged communications (SMS/email/WhatsApp/push) for a seller.
 * @param {string|number} sellerProfileId - The seller's profile ID.
 * @returns {Promise<Object>} A promise that resolves to { seller_profile_id, logs: [...] }.
 */
export const getSellerCommsHistory = async (sellerProfileId) => {
  if (!sellerProfileId) throw new Error("Seller profile ID is required.");
  try {
    const response = await api.get(`/admin/sellers/${sellerProfileId}/comms-history`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching comms history for seller ${sellerProfileId}:`, error);
    throw error;
  }
};

/**
 * Fetches the ENTIRE WhatsApp conversation for the buyer behind a lead
 * (all outbound + inbound messages, including unsolicited ones), ordered chronologically.
 * @param {string|number} leadId - The ID of the lead.
 * @returns {Promise<Object>} A promise that resolves to { buyer_id, phone, messages: [...] }.
 */
export const getLeadConversation = async (leadId) => {
  if (!leadId) throw new Error("Lead ID is required.");
  try {
    const response = await api.get(`/admin/lead-info/${leadId}/conversations`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching conversation for lead ${leadId}:`, error);
    throw error;
  }
};

/**
 * Fetches the seller's active Standard and Welcome coin packages for the admin calculator.
 * @param {string|number} sellerProfileId - The ID of the seller profile.
 * @returns {Promise<Array>} A promise that resolves to an array of package objects.
 */
export const getSellerStandardWelcomeSets = async (sellerProfileId) => {
  try {
    const response = await api.get(`/admin/sellers/${sellerProfileId}/standard-welcome-sets`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching standard/welcome sets for seller ${sellerProfileId}:`, error);
    throw error;
  }
};


/** admin notes
 * Fetches all active notes for a specific context.
 * @param {Object} params - The query parameters.
 * @param {string} params.context_type - The entity type (e.g., "SELLER").
 * @param {number} params.context_id - The entity ID (e.g., sellerProfileId).
 * @param {string} params.context_tab - The specific tab (e.g., "PAYMENTS").
 * @returns {Promise<Array>} A promise that resolves to an array of note objects.
 */
export const getAdminNotes = async ({ context_type, context_id, context_tab }) => {
  try {
    const response = await api.get("/admin/notes/", {
      params: { context_type, context_id, context_tab },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching admin notes:", error);
    throw error;
  }
};

/**
 * Creates a new admin note.
 * @param {Object} payload - The note data to create.
 * @param {string} payload.content - The text of the note.
 * @param {string} payload.context_type - The entity type (e.g., "SELLER").
 * @param {number} payload.context_id - The entity ID (e.g., sellerProfileId).
 * @param {string} payload.context_tab - The specific tab (e.g., "PAYMENTS").
 * @returns {Promise<Object>} A promise that resolves to the newly created note.
 */
export const createAdminNote = async (payload) => {
  try {
    const response = await api.post("/admin/notes/", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating admin note:", error);
    throw error;
  }
};

/**
 * Updates an existing admin note.
 * @param {number} noteId - The ID of the note to update.
 * @param {string} content - The new content for the note.
 * @returns {Promise<Object>} A promise that resolves to the updated note.
 */
export const updateAdminNote = async (noteId, content) => {
  try {
    const response = await api.put(`/admin/notes/${noteId}`, { content });
    return response.data;
  } catch (error) {
    console.error(`Error updating note ${noteId}:`, error);
    throw error;
  }
};

/**
 * Deactivates (soft deletes) an admin note.
 * @param {number} noteId - The ID of the note to delete.
 * @returns {Promise<void>} A promise that resolves when the note is deleted.
 */
export const deleteAdminNote = async (noteId) => {
  try {
    await api.delete(`/admin/notes/${noteId}`);
  } catch (error) {
    console.error(`Error deleting note ${noteId}:`, error);
    throw error;
  }
};

/**
 * Fetches the seller's GST and Billing details.
 * @param {string|number} sellerProfileId
 * @returns {Promise<Object>}
 */
export const getSellerBillingDetails = async (sellerProfileId) => {
  try {
    const response = await api.get(`/admin/financials/${sellerProfileId}/billing-details`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching billing details for seller ${sellerProfileId}:`, error);
    throw error;
  }
};


export const getLeadReturnAnalytics = async (sellerProfileId) => {
  try {
    const response = await api.get(
      `/admin/sellers/${sellerProfileId}/lead-return-analytics`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching lead return analytics for seller ${sellerProfileId}:`,
      error
    );
    throw error;
  }
};

// src/services/AdminApis.js

/**
 * Fetches pricing configuration (Base coins, Base rate, Tax) for a category.
 * @param {string|number} categoryId
 * @returns {Promise<Object>}
 */
export const getCategoryPricingConfig = async (categoryId) => {
    try {
        const response = await api.get(`/admin/prospect/${categoryId}/pricing-config`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching pricing config for category ${categoryId}:`, error);
        throw error;
    }
};

// src/services/AdminApis.js

/**
 * Fetches the seller's categories with their base coin pricing.
 * @param {string|number} sellerProfileId 
 * @returns {Promise<Array>} List of category objects with pricing info.
 */
export const getSellerCategoryPricingOptions = async (sellerProfileId) => {
    try {
        const response = await api.get(`/admin/sellers/${sellerProfileId}/category-pricing-options`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching category pricing options:`, error);
        throw error;
    }
};

/**
 * Regenerates the welcome set based on a new category.
 * @param {string|number} sellerProfileId 
 * @param {object} payload { new_master_category_id, reason }
 */
export const regenerateWelcomeSet = async (sellerProfileId, payload) => {
    try {
        const response = await api.post(`/admin/sellers/${sellerProfileId}/regenerate-welcome-set`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error regenerating welcome set:`, error);
        throw error;
    }
};


// src/services/AdminApis.js (Append these)

/**
 * Creates a new referral code for the logged-in admin.
 * @param {Object} payload { custom_code: string, note: string }
 */
export const createReferralCode = async (payload) => {
    try {
        const response = await api.post('/admin/prospect/create-code', payload);
        return response.data;
    } catch (error) {
        console.error("Error creating referral code:", error);
        throw error;
    }
};

/**
 * Fetches dashboard stats for the logged-in admin employee.
 */
export const getEmployeeReferralStats = async () => {
    try {
        const response = await api.get('/admin/prospect/dashboard');
        return response.data;
    } catch (error) {
        console.error("Error fetching referral stats:", error);
        throw error;
    }
};

// src/services/AdminApis.js

/**
 * Saves a generated referral link for later use.
 * @param {Object} payload 
 */
export const saveReferralLink = async (payload) => {
    try {
        const response = await api.post('/admin/prospect/save-link', payload);
        return response.data;
    } catch (error) {
        console.error("Error saving referral link:", error);
        throw error;
    }
};

/**
 * Fetches the seller's Welcome Set Coin Reset Assurance (CRA) eligibility verdict.
 * @param {string|number} sellerProfileId
 * @returns {Promise<Object>} Eligibility, per-criterion breakdown and Welcome Set summary.
 */
export const getCoinResetAssurance = async (sellerProfileId) => {
    const response = await api.get(`/admin/sellers/${sellerProfileId}/coin-reset-assurance`);
    return response.data;
};

/**
 * Returns 100% of the seller's Welcome Set coins with a 30-day expiry.
 * @param {string|number} sellerProfileId
 * @param {Object} payload
 * @param {string} payload.reason - Mandatory reason, stored on the claim and the admin log.
 * @param {boolean} [payload.is_override] - Grant despite failing criteria (senior admins only).
 */
export const grantCoinResetAssurance = async (sellerProfileId, payload) => {
    const response = await api.post(`/admin/sellers/${sellerProfileId}/coin-reset-assurance/grant`, payload);
    return response.data;
};
